import type { MinRole } from "./roles";
import type { IconName } from "./icons";

/**
 * The nav registry — the shell's extension point. A feature ships a screen by adding ONE entry
 * (path segment + label key + minimum role + the venue-scoped query-key resources it owns) and
 * registering a route; the guard + nav read the same `minRole`. Nothing else is required.
 *
 * The rail is grouped by PERSONA, not by feature area, so it reads the way a restaurant thinks:
 *   - `service`  — day-of-service tools every waiter uses (WAITER-accessible).
 *   - `gestion`  — back-office management + the venue Settings hub (MANAGER).
 *   - `account`  — the account/subscription surface: Facturation (company/VAT profile, plan,
 *                  Stripe invoices). MANAGER; the entry is account-root-aware server-side (R1).
 * Venue CONFIGURATION does not live in the rail: the single `settings` entry opens a Settings hub
 * page whose own sub-navigation is `SETTINGS_SECTIONS` below. This keeps the rail short (~12 links)
 * instead of the old flat 19. Section order is fixed by NAV_SECTIONS.
 */
export const NAV_SECTIONS = ["service", "gestion", "account"] as const;
export type NavSection = (typeof NAV_SECTIONS)[number];

/** Section header i18n label keys. */
export const SECTION_LABEL_KEYS: Record<NavSection, string> = {
  service: "shell.nav.section.service",
  gestion: "shell.nav.section.gestion",
  account: "shell.nav.section.account",
};

export interface NavEntry {
  /** Path segment under `/v/:venueId` (e.g. "overview", "settings/general"). */
  to: string;
  /** i18n key for the nav label. */
  labelKey: string;
  /** Advisory minimum role to see the entry AND pass its route guard. */
  minRole: MinRole;
  /** The `["venue", venueId, <resource>]` prefixes this screen's queries use (D.6 convention). */
  resources: string[];
  /** Which sidebar section the link groups under (rail entries only). */
  section?: NavSection;
  /** The nav glyph (see shell/icons). */
  icon: IconName;
  /**
   * Optional entitlement (an `EntitlementKey` name) required to SEE this entry AND pass its route guard.
   * Absent ⇒ no feature gate (role only). The nav filter and `RequireEntitlement` read the same venue
   * flags, so a hidden feature can never be reached, and the server re-enforces every action fail-closed.
   */
  feature?: string;
  /**
   * Whether the destination only actually FUNCTIONS once the venue is publicly LIVE (the booking-output
   * surfaces). Advisory: it doesn't hide/disable the config screen (you set these up before going live);
   * the screen surfaces a "preview until live" notice via useFeatureAvailability / useOnboardingProgress.
   */
  requiresLive?: boolean;
  /** Sub-nav placeholder that is not yet a real destination — rendered disabled ("bientôt"). */
  disabled?: boolean;
  /**
   * HARD-HIDE this entry unless the venue's no-show guarantee is enabled (settings → Garantie). Unlike
   * `feature` (which LOCKS a still-visible row), this removes the entry entirely — an output surface that
   * only exists once the owner turns the guarantee on. Read from `useGuaranteePolicy()` in the nav filter.
   */
  requiresNoShowPolicy?: boolean;
  /** HARD-HIDE unless deposit capture is live (always hidden for now — the capture half is unbuilt). */
  requiresDepositPolicy?: boolean;
}

/** Primary left-rail entries, grouped by persona. */
export const NAV_ENTRIES: ReadonlyArray<NavEntry> = [
  // — Service (day of service, WAITER+) —
  {
    to: "overview",
    labelKey: "shell.nav.overview",
    minRole: "WAITER",
    resources: ["bookings", "availability"],
    section: "service",
    icon: "overview",
  },
  {
    to: "live",
    labelKey: "shell.nav.live",
    minRole: "WAITER",
    resources: ["bookings", "availability"],
    section: "service",
    icon: "live",
  },
  {
    to: "bookings",
    labelKey: "shell.nav.bookings",
    minRole: "WAITER",
    resources: ["bookings", "availability"],
    section: "service",
    icon: "bookings",
  },
  {
    to: "waitlist",
    labelKey: "shell.nav.waitlist",
    minRole: "WAITER",
    resources: ["waitlist"],
    section: "service",
    icon: "waitlist",
    feature: "WAITLIST",
  },
  {
    to: "guests",
    labelKey: "shell.nav.guests",
    minRole: "WAITER",
    resources: ["guests"],
    section: "service",
    icon: "guests",
  },
  {
    // Hours & services: waiters VIEW (read-only), managers edit. Operational enough to sit in Service
    // rather than buried in the manager Settings hub. Moved from the old `settings/schedule` path.
    to: "schedule",
    labelKey: "shell.nav.schedule",
    minRole: "WAITER",
    resources: ["services"],
    section: "service",
    icon: "schedule",
  },
  // — Gestion (back office, MANAGER) —
  {
    to: "blacklist",
    labelKey: "shell.nav.blacklist",
    minRole: "MANAGER",
    resources: ["guests"],
    section: "gestion",
    icon: "blacklist",
  },
  {
    to: "analytics",
    labelKey: "shell.nav.analytics",
    minRole: "MANAGER",
    resources: ["analytics"],
    section: "gestion",
    icon: "analytics",
  },
  {
    // Facturation is an ACCOUNT concern, not a per-establishment one: it carries the account's
    // company/VAT profile (BillingProfileCard, account-root only), the plan/subscription, and the
    // Stripe invoice portal. So it groups under `account` ("Compte"), not the operational `gestion`.
    to: "billing",
    labelKey: "shell.nav.billing",
    minRole: "MANAGER",
    resources: ["billing"],
    section: "account",
    icon: "billing",
  },
  {
    to: "deposits",
    labelKey: "shell.nav.deposits",
    minRole: "MANAGER",
    resources: ["deposits"],
    section: "gestion",
    icon: "billing",
    // Hidden until deposit capture ships (see requiresDepositPolicy) — the Acomptes page can't populate yet.
    requiresDepositPolicy: true,
  },
  {
    to: "guarantees/failed",
    labelKey: "app.noShowCharge.failedList.title",
    minRole: "MANAGER",
    resources: ["guarantees"],
    section: "gestion",
    icon: "billing",
    // Only shown once the owner enables the no-show guarantee in settings → Garantie.
    requiresNoShowPolicy: true,
  },
  {
    // "Mon site web" — the public storefront editor. Promoted out of the Settings hub to its own
    // Gestion rail entry (it's a primary surface, not a config detail). `to: "site"` is a top-level
    // route (NOT settings/*), so the editor renders with its own PageScaffold chrome, not the settings
    // sub-nav; the old settings/site link redirects here.
    to: "site",
    labelKey: "shell.nav.mySite",
    minRole: "MANAGER",
    resources: ["site"],
    section: "gestion",
    icon: "site",
  },
  {
    // The venue Settings hub — one rail entry that opens a dedicated page (SettingsLayout) whose own
    // sub-navigation is SETTINGS_SECTIONS. This is what keeps the rail short.
    to: "settings",
    labelKey: "shell.nav.settings",
    minRole: "MANAGER",
    resources: [],
    section: "gestion",
    icon: "settings",
  },
  // — Account ("Compte"): the account/subscription surface. Facturation (billing entry above, grouped
  //   into `account`) lives here. There is deliberately no personal-profile screen — identity comes
  //   from Google and sign-out is in the top header, not the rail. —
];

/**
 * Settings hub sub-navigation — the venue CONFIGURATION destinations, rendered inside the Settings
 * page (SettingsLayout), not the rail. Same shape as NAV_ENTRIES (so role/entitlement filtering is
 * identical) minus `section`. All MANAGER, matching the hub's route guard.
 */
export const SETTINGS_SECTIONS: ReadonlyArray<NavEntry> = [
  {
    to: "settings/general",
    labelKey: "shell.settings.general",
    minRole: "MANAGER",
    resources: ["services"],
    icon: "settings",
  },
  {
    to: "settings/floor-plan",
    labelKey: "shell.nav.floorPlan",
    minRole: "MANAGER",
    resources: ["floorplan"],
    icon: "floorPlan",
    feature: "FLOOR_PLAN",
  },
  {
    to: "settings/media",
    labelKey: "shell.nav.media",
    minRole: "MANAGER",
    resources: ["media"],
    icon: "media",
  },
  {
    // Garantie anti no-show — the venue-wide guarantee/deposit config (enable + fee). Writes the
    // booking-policy; enabling it activates the guest card-capture flow and reveals the failed-charges surface.
    to: "settings/guarantee",
    labelKey: "shell.settings.guarantee",
    minRole: "MANAGER",
    resources: ["services"],
    icon: "billing",
  },
  {
    // Mon code PIN — the owner's or manager's own PIN, the one a no-show charge and a waiter's
    // cancellation ask for. The server re-checks AUTHORIZE_SENSITIVE_ACTION on every call.
    to: "settings/pin",
    labelKey: "shell.settings.pin",
    minRole: "MANAGER",
    resources: ["staff"],
    icon: "lock",
  },
  {
    // Canaux de réservation — the merged Widget (own site) / Réseaux (Meta) / Google (RwG) surface.
    // The booking page only goes live once the venue is published, so the page shows a preview notice.
    to: "settings/channels",
    labelKey: "shell.nav.channels",
    minRole: "MANAGER",
    resources: ["place-match"],
    icon: "channels",
    requiresLive: true,
  },
  {
    to: "settings/abuse-blocks",
    labelKey: "shell.nav.abuseBlocks",
    minRole: "MANAGER",
    resources: ["abuse-blocks"],
    icon: "abuseBlocks",
  },
  {
    // The onboarding wizard stays a top-level full-screen route; it's surfaced here in the hub sub-nav.
    to: "onboarding",
    labelKey: "shell.nav.onboarding",
    minRole: "MANAGER",
    resources: ["onboarding"],
    icon: "onboarding",
  },
  {
    // Staff & roles — the page isn't built yet (the PIN/roles flow is backend); shown disabled.
    to: "settings/staff",
    labelKey: "shell.nav.staff",
    minRole: "MANAGER",
    resources: ["staff"],
    icon: "staff",
    disabled: true,
  },
];
